import { TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { useState } from 'react';
import { CustomTabWithStep } from '@/components/ats/Tabs';
import { PlatformLogical } from '@/types/ext_assessment';

interface TabConfig {
  id: string;
  label: string;
  component: React.ComponentType<any>;
  props?: Record<string, any>;
}

interface AssessmentTabsProps {
  tabs: TabConfig[];
  isCreateFlow?: boolean;
  onTabChange?: (tabId: string) => void;
  onTabValidation?: (fromTab: string, toTab: string) => Promise<boolean>;
  initialTab?: string;
  currentTab?: string;
  activity?: any;
  platform: PlatformLogical;
}

export const AssessmentTabs = ({
  tabs,
  isCreateFlow = false,
  onTabChange,
  onTabValidation,
  initialTab = 'details',
  currentTab: externalCurrentTab,
  activity,
  platform,
}: AssessmentTabsProps) => {
  const [internalCurrentTab, setInternalCurrentTab] = useState(initialTab);
  const [tabVisited, setTabVisited] = useState([initialTab]);

  const currentTab = externalCurrentTab || internalCurrentTab;
  const setCurrentTab = onTabChange || setInternalCurrentTab;

  const handleTabClick = async (tabId: string) => {
    if (isCreateFlow && !tabVisited?.includes(tabId)) return;

    // Validate current tab before switching
    if (onTabValidation) {
      const isValid = await onTabValidation(currentTab, tabId);
      if (!isValid) return;
    }

    if (!tabVisited?.includes(tabId)) {
      setTabVisited((prev) => [...prev, tabId]);
    }

    setCurrentTab(tabId);
  };

  return (
    <Tabs
      mt="lg"
      display="flex"
      flexDir="column"
      flex="1"
      h="calc(100% - 130px)"
      maxH="calc(100% - 130px)"
      alignItems="stretch"
      index={tabs?.findIndex((option) => option?.id === currentTab)}
    >
      <TabList>
        {tabs.map((option, idx) => {
          return (
            <CustomTabWithStep
              key={option.id}
              label={option?.label}
              // @ts-ignore
              step={isCreateFlow ? idx + 1 : ''}
              arrow={isCreateFlow && option?.id !== tabs[tabs.length - 1]?.id}
              onClick={() => handleTabClick(option?.id)}
            />
          );
        })}
      </TabList>
      <TabPanels maxH="calc(100% - 40px)">
        {tabs.map((option) => {
          const Component = option.component;
          return (
            <TabPanel
              p="0"
              key={option?.id}
              maxH="100%"
              flex="1"
              overflowY="auto"
              mb="70px"
            >
              <Component
                {...option?.props}
                activity={activity}
                platform={platform}
              />
            </TabPanel>
          );
        })}
      </TabPanels>
    </Tabs>
  );
};
